<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Verify Occupation Certificate</span>
            </nav>
            <h1 class="page-title">Verify Public Health Occupation Certificate</h1>
            <p class="page-subtitle">Confirm a Department of Public Health occupation certificate by registration number.</p>
          </div>
        </div>
      </section>

      <!-- Content -->
      <section class="content-section">
        <div class="container" style="max-width: 880px;">

          <!-- Search box -->
          <div class="verify-card">
            <label class="verify-label">Registration Number</label>
            <div class="verify-row">
              <input type="text" class="verify-input" placeholder="e.g. OCC/2026/00004"
                     v-model="registrationNo" @keyup.enter="lookup" :disabled="loading">
              <button class="verify-btn" @click="lookup" :disabled="loading || !registrationNo.trim()">
                <span v-if="loading">Verifying…</span>
                <span v-else>Verify</span>
              </button>
            </div>
            <small class="verify-hint">Tip: scan the QR code on a printed certificate to verify it instantly.</small>
          </div>

          <!-- Result -->
          <div v-if="loaded">
            <!-- No record -->
            <div v-if="!premise" class="verify-status status-unknown">
              <div class="status-icon">?</div>
              <div>
                <h3>No record found</h3>
                <p>We couldn't find a premise registered with <code>{{ lastQueried }}</code>. Check the number and try again.</p>
              </div>
            </div>

            <!-- Has a record -->
            <div v-else>
              <!-- Verdict banner -->
              <div class="verify-status" :class="verdictClass">
                <div class="status-icon">{{ verdictIcon }}</div>
                <div>
                  <h3>{{ verdictTitle }}</h3>
                  <p>{{ verdictDescription }}</p>
                </div>
              </div>

              <!-- Detail grid -->
              <div class="verify-details">
                <div class="detail-card">
                  <h4>Premises</h4>
                  <div class="detail-row"><span class="detail-label">Name</span><span class="detail-value">{{ show(premise.premisesName) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Occupant</span><span class="detail-value">{{ show(premise.occupantName) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Registration No</span><span class="detail-value">{{ show(premise.registrationNo) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Plot No.</span><span class="detail-value">{{ show(premise.plotNumber) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Use</span><span class="detail-value">{{ show(premise.premiseUse) }}</span></div>
                </div>

                <div class="detail-card">
                  <h4>Location</h4>
                  <div class="detail-row"><span class="detail-label">Physical Location</span><span class="detail-value">{{ show(premise.physicalLocation) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Ward</span><span class="detail-value">{{ show(premise.wardName) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Sub County</span><span class="detail-value">{{ show(premise.subCountyName) }}</span></div>
                  <div class="detail-row"><span class="detail-label">County</span><span class="detail-value">{{ show(premise.countyName || 'Kajiado') }}</span></div>
                </div>

                <div class="detail-card">
                  <h4>Certificate</h4>
                  <div class="detail-row"><span class="detail-label">Status</span><span class="detail-value">{{ show(premise.registrationStatus) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Inspection Date</span><span class="detail-value">{{ formatDate(premise.inspectionDate) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Approval Date</span><span class="detail-value">{{ formatDate(premise.approvedAt) }}</span></div>
                  <div class="detail-row"><span class="detail-label">Expiry Date</span><span class="detail-value">{{ formatDate(premise.expiryDate) }}</span></div>
                  <div v-if="Number(premise.renewalCount) > 0" class="detail-row"><span class="detail-label">Renewals</span><span class="detail-value">{{ premise.renewalCount }}</span></div>
                </div>
              </div>

              <p class="verify-footer">
                Verified at {{ formatDateTime(premise.verifiedAt) }} ·
                If anything here looks wrong, contact the Department of Public Health on
                <a href="https://kajiado.craftcollect.africa">kajiado.craftcollect.africa</a>.
              </p>
            </div>
          </div>

        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import { execute, occupational } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "VerifyPremise",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      registrationNo: '',
      loading: false,
      loaded: false,
      premise: null,
      lastQueried: '',
    };
  },
  computed: {
    verdictClass() {
      if (!this.premise) return '';
      if (this.premise.valid)   return 'status-valid';
      if (this.premise.expired) return 'status-expired';
      const s = String(this.premise.registrationStatus || '').toUpperCase();
      if (s === 'REJECTED' || s === 'SUSPENDED') return 'status-bad';
      return 'status-pending';
    },
    verdictIcon() {
      if (!this.premise) return '?';
      if (this.premise.valid)   return '✓';
      if (this.premise.expired) return '!';
      return '…';
    },
    verdictTitle() {
      if (!this.premise) return '';
      if (this.premise.valid)   return 'Certificate is VALID';
      if (this.premise.expired) return 'Certificate has EXPIRED';
      const s = String(this.premise.registrationStatus || '').toUpperCase();
      if (s === 'REJECTED')  return 'Application REJECTED';
      if (s === 'SUSPENDED') return 'Certificate SUSPENDED';
      if (s === 'PENDING')   return 'Awaiting Approval';
      return 'Status: ' + (this.premise.registrationStatus || 'Unknown');
    },
    verdictDescription() {
      if (!this.premise) return '';
      if (this.premise.valid) {
        const d = this.premise.daysToExpiry;
        return d != null ? `Active. Expires in ${d} day${d === 1 ? '' : 's'}.` : 'Active and in good standing.';
      }
      if (this.premise.expired) {
        return 'This certificate is past its expiry date. The owner should renew before continuing occupation.';
      }
      const s = String(this.premise.registrationStatus || '').toUpperCase();
      if (s === 'REJECTED')  return this.premise.rejectionReason || 'The registration application was rejected.';
      if (s === 'SUSPENDED') return 'This certificate has been suspended by the Department of Public Health.';
      if (s === 'PENDING')   return 'A certificate has not yet been issued for this registration.';
      return '';
    },
  },
  mounted() {
    // QR scan deep-link: /verify-premise/OCC%2F2026%2F00004
    const fromParam = this.$route.params.registrationNo;
    const fromQuery = this.$route.query.registrationNo || this.$route.query.premiseId;
    if (fromParam) {
      this.registrationNo = decodeURIComponent(fromParam);
      this.lookup();
    } else if (fromQuery) {
      this.registrationNo = String(fromQuery);
      this.lookup();
    }
  },
  methods: {
    show(v) {
      return (v === null || v === undefined || v === '') ? '—' : v;
    },

    async lookup() {
      const reg = this.registrationNo.trim();
      if (!reg) return;
      this.loading = true;
      this.lastQueried = reg;
      try {
        const fd = new FormData();
        fd.append('function', 'verifyPremise');
        // Numeric → assume premiseId; otherwise registrationNo (slashes and all).
        if (/^\d+$/.test(reg)) {
          fd.append('premiseId', reg);
        } else {
          fd.append('registrationNo', reg);
        }
        const res = await execute(fd, occupational);
        const j = res.data;
        if (j && j.success && j.data && j.data.premise && j.data.premise.found) {
          this.premise = j.data.premise;
        } else {
          this.premise = null;
        }
        this.loaded = true;
      } catch (e) {
        console.error('verify lookup failed', e);
        this.premise = null;
        this.loaded = true;
      }
      this.loading = false;
    },
    formatDate(d) {
      if (!d) return '—';
      const s = String(d);
      if (s === '0000-00-00' || s === '0000-00-00 00:00:00') return '—';
      const dt = new Date(s.replace(' ', 'T'));
      if (isNaN(dt.getTime())) return s;
      return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    formatDateTime(d) {
      if (!d) return '';
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? d : dt.toLocaleString('en-GB');
    },
  },
};
</script>

<style scoped>
.verify-card {
  background: #fff; border: 1px solid #e6e8eb; border-radius: 12px;
  padding: 24px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); margin-bottom: 24px;
}
.verify-label { display: block; font-weight: 600; font-size: 13px; color: #344054; margin-bottom: 8px; }
.verify-row   { display: flex; gap: 8px; }
.verify-input {
  flex: 1; padding: 12px 14px; border: 1px solid #d0d5dd; border-radius: 8px;
  font-size: 15px; outline: none; transition: border-color .15s, box-shadow .15s;
}
.verify-input:focus { border-color: #0d6efd; box-shadow: 0 0 0 3px rgba(13,110,253,0.15); }
.verify-btn {
  background: #0d6efd; color: #fff; border: none; border-radius: 8px;
  padding: 12px 20px; font-weight: 600; cursor: pointer; min-width: 120px;
}
.verify-btn:disabled { opacity: .55; cursor: not-allowed; }
.verify-hint { display: block; margin-top: 8px; color: #667085; font-size: 12px; }

.verify-status {
  display: flex; gap: 16px; padding: 20px; border-radius: 12px;
  margin-bottom: 24px; border: 1px solid transparent;
}
.verify-status h3 { margin: 0 0 4px 0; font-size: 20px; }
.verify-status p  { margin: 0; opacity: .9; }
.status-icon {
  width: 48px; height: 48px; border-radius: 50%; display:flex;
  align-items:center; justify-content:center; font-size:24px; font-weight:700; flex-shrink:0;
}
.status-valid    { background: #ecfdf3; border-color: #abefc6; color: #027a48; }
.status-valid    .status-icon { background: #027a48; color: white; }
.status-expired  { background: #fff7ed; border-color: #fed7aa; color: #b54708; }
.status-expired  .status-icon { background: #b54708; color: white; }
.status-bad      { background: #fef3f2; border-color: #fecdca; color: #b42318; }
.status-bad      .status-icon { background: #b42318; color: white; }
.status-pending  { background: #fffaeb; border-color: #fedf89; color: #b54708; }
.status-pending  .status-icon { background: #b54708; color: white; }
.status-unknown  { background: #f9fafb; border-color: #e6e8eb; color: #344054; }
.status-unknown  .status-icon { background: #98a2b3; color: white; }

.verify-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
.detail-card    { background: #fff; border: 1px solid #e6e8eb; border-radius: 12px; padding: 20px; }
.detail-card h4 { margin: 0 0 12px 0; font-size: 14px; color: #0d6efd; text-transform: uppercase; letter-spacing:.5px; }
.detail-row     { display: flex; justify-content: space-between; gap: 8px; padding: 6px 0; font-size: 14px; border-bottom: 1px dashed #f1f3f5; }
.detail-row:last-child { border-bottom: none; }
.detail-label   { color: #667085; }
.detail-value   { font-weight: 600; color: #101828; text-align: right; }

.verify-footer { margin-top: 18px; color: #667085; font-size: 12px; text-align: center; }
</style>
